/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, May 8th 2022, 7:57:01 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

// import ArchiveIcon from "@mui/icons-material/Archive";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import PaymentIcon from "@mui/icons-material/Payment";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import RepeatIcon from "@mui/icons-material/Repeat";
import UndoIcon from "@mui/icons-material/Undo";
import AssessmentIcon from "@mui/icons-material/Assessment";
import PollIcon from "@mui/icons-material/Poll";
// import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PostAddIcon from "@mui/icons-material/PostAdd";
import MessageIcon from "@mui/icons-material/Message";
import ApiIcon from "@mui/icons-material/Api";
import StorageIcon from "@mui/icons-material/Storage";
import PersonIcon from "@mui/icons-material/Person";
import {
  AUTOMATED_TASK_TYPES,
  USER_TASK_TYPES,
  NODE_TYPES,
  Permission,
  TaskTemplate,
  IUserTaskTypesData,
} from "lib/schemas";
import { SigningIcon, RobotIcon } from "theme/icons";
import { COLORSCHEME } from "theme/styles";
import { StyledOSDialog } from "ui-widgets";
import { AIAgentConfigurationForm } from "ai-workers/components";

/** Widget and class Names for the different BPMN node types */
export const NODE_TYPE_WIDGETS: Partial<Record<keyof typeof NODE_TYPES, { nodeTypeCssClassName: string }>> = {
  StartFunction: { nodeTypeCssClassName: "os-start-node" },
  View: { nodeTypeCssClassName: "os-user-task-node" },
  Call: { nodeTypeCssClassName: "os-call-activity-node" },
  Handler: { nodeTypeCssClassName: "os-automated-service-node" },
  Split: { nodeTypeCssClassName: "os-conditional-node" },
  Join: { nodeTypeCssClassName: "os-merge-node" },
  Job: { nodeTypeCssClassName: "os-scheduled-node" },
  End: { nodeTypeCssClassName: "os-end-node" },
};

export interface AdditionalInfoComponentProps {
  saveNewTaskInfo: (optionalProperties: Partial<TaskTemplate>) => void;
  isOpen: boolean;
  handleClose: () => void;
}

type IUserTaskTypeWidget = {
  userTaskTypeIcon: React.ReactNode;
  userTaskTypeTitle: string;
  userTaskTypeDescription: string;
  AdditionalInfoComponent?: React.ComponentType<AdditionalInfoComponentProps>;
};

export const getUserTaskTypeWidgets = (
  userTaskTypesData: IUserTaskTypesData
): { [key: string]: IUserTaskTypeWidget } => {
  const fetchedUserTaskTypeWidgets = Object.keys(userTaskTypesData).reduce((prev, curr) => {
    prev[curr] = {
      userTaskTypeIcon: <PersonIcon color="primary" fontSize="large" />,
      userTaskTypeTitle: userTaskTypesData[curr].name,
      userTaskTypeDescription: userTaskTypesData[curr].description,
    };
    return prev;
  }, {});

  return { ...fetchedUserTaskTypeWidgets, ...INBUILT_USER_TASK_TYPE_WIDGETS };
};

/** Some task types have been commented out for the admin console MVP. Add them and the corresponding functionality
 * when needed.
 */

const INBUILT_USER_TASK_TYPE_WIDGETS: {
  [K in keyof typeof USER_TASK_TYPES]?: IUserTaskTypeWidget;
} = {
  move: {
    userTaskTypeIcon: <DriveFileMoveIcon color="primary" fontSize="large" />,
    userTaskTypeTitle: "Forward Transition",
    userTaskTypeDescription:
      "This is a simple task where the user just clicks a button to move the workflow to the next configured step.",
  },
  resubmission: {
    userTaskTypeIcon: <RepeatIcon color="primary" fontSize="large" />,
    userTaskTypeTitle: "Resubmission Task (By Submiiter)",
    userTaskTypeDescription: "A resubmission task is carried out by the initiator of the process",
  },
  billing: {
    userTaskTypeIcon: <ReceiptIcon color="primary" fontSize="large" />,
    userTaskTypeTitle: "Billing",
    userTaskTypeDescription: "A billing task allows a user to generate a bill for payment",
  },
  payment: {
    userTaskTypeIcon: <PaymentIcon color="primary" fontSize="large" />,
    userTaskTypeTitle: "Payment",
    userTaskTypeDescription: "A payment task will prompt a payment for a generated bill",
  },
  payment_confirmation: {
    userTaskTypeIcon: <AccountBalanceIcon color="primary" fontSize="large" />,
    userTaskTypeTitle: "Payment Confirmation",
    userTaskTypeDescription:
      "A Payment Confirmation task is for the confirmation of payments made manually i.e. upload of payment receipts, bank slips etc.",
  },
  review: {
    userTaskTypeIcon: <AssessmentIcon color="primary" fontSize="large" />,
    userTaskTypeTitle: "Assessment Task",
    userTaskTypeDescription:
      "A review task involves a user performing an assessment in relation to the submission made. This can be departmental reviews, site visits, inspections etc.",
  },
  sign: {
    userTaskTypeIcon: <SigningIcon fill={COLORSCHEME.primary} width={30} height={30} />,
    userTaskTypeTitle: "Digital Signing Task",
    userTaskTypeDescription: "Digital signature tasks allow user(s) to sign generated documents electronically.",
  },
  //   archive: [
  //     <ArchiveIcon color="primary" fontSize="large" />,
  //     "Archive",
  //     "An Archive task allows a user to archive a process workflows",
  //   ],
  assignment: {
    userTaskTypeIcon: <AssignmentIndIcon color="primary" fontSize="large" />,
    userTaskTypeTitle: "Process Assignment Task",
    userTaskTypeDescription:
      "An process assignment task assigns the whole instance of a single process to a specific user. The user assigned will by default handle all other user tasks in the workflow instance they have been assigned.",
  },
  //   custom: [<SettingsApplicationsIcon color="primary" fontSize="large" />, "Custom", "Create a custom user Task"],
  //   generate_document: [<PostAddIcon color="primary" fontSize="large" />, "Generate Document", "User Generated Document Task"],
  survey: {
    userTaskTypeIcon: <PollIcon color="primary" fontSize="large" />,
    userTaskTypeTitle: "Survey (Submitter)",
    userTaskTypeDescription: "A survey task allows the initiator of the process to fill in a survey form.",
  },
};

export const SUPPORTED_AUTOMATED_TASK_TYPES_WIDGETS: {
  [K in keyof typeof AUTOMATED_TASK_TYPES]?: {
    automatedTaskTypeIcon: React.ReactNode;
    automatedTaskTypeTitle: string;
    automatedTaskTypeDescription: string;
    AdditionalInfoComponent?: React.ComponentType<AdditionalInfoComponentProps>;
  };
} = {
  ai_task: {
    automatedTaskTypeIcon: <RobotIcon fill={COLORSCHEME.primary} width={30} height={30} />,
    automatedTaskTypeTitle: "Artificial Intelligence Task",
    automatedTaskTypeDescription: "Automatically performs a task using AI",
    AdditionalInfoComponent: ({ handleClose, isOpen, saveNewTaskInfo }) => (
      <StyledOSDialog
        open={isOpen}
        onClose={handleClose}
        popUpIcon={<RobotIcon fill={COLORSCHEME.primary} width={30} height={30} />}
        popUpTitle="New Configuration for Artificial Intelligence Agent"
      >
        <AIAgentConfigurationForm
          updateAIAgentConfigurationFromAPICallback={(aiAgentConfiguration) =>
            saveNewTaskInfo({ ai_task_instruction: aiAgentConfiguration?.id })
          }
          aiAgentConfigurationDefaultValues={null}
          aiAgentConfigurationFetchId={null}
        />
      </StyledOSDialog>
    ),
  },
  send_notification: {
    automatedTaskTypeIcon: <MessageIcon color="primary" fontSize="large" />,
    automatedTaskTypeTitle: "Send Notification",
    automatedTaskTypeDescription: "Automatically sends an email or SMS notification",
  },
  auto_billing: {
    automatedTaskTypeIcon: <ReceiptIcon color="primary" fontSize="large" />,
    automatedTaskTypeTitle: "Auto Generate a Bill",
    automatedTaskTypeDescription: "Automatically generates a bill for payment",
  },
  generate_document: {
    automatedTaskTypeIcon: <PostAddIcon color="primary" fontSize="large" />,
    automatedTaskTypeTitle: "Generate Document",
    automatedTaskTypeDescription: "Automatically generate a document from a configured document template",
  },
  update_dataset: {
    automatedTaskTypeIcon: <StorageIcon color="primary" fontSize="large" />,
    automatedTaskTypeTitle: "Update a Dataset",
    automatedTaskTypeDescription: "Automatically updates a dataset that you have created on Onestop.",
  },
  web_service_call: {
    automatedTaskTypeIcon: <ApiIcon color="primary" fontSize="large" />,
    automatedTaskTypeTitle: "Call a Web Service",
    automatedTaskTypeDescription: `Automatically posts data in a prescribed format to a provided web service endpoint. 
      Onestop currently only supports SOAP and HTTP. This is basically used for remote system integration. 
      Need more complex integration? Try Onestop's integrations feature and contact us if you do not see the platform you want to integrate to in our list.`,
  },
};

export interface TASK_ACCESS_PERMISSION_WIDGETS {
  title: string;
  description: string;
  permissionId: Permission["id"];
  icon: React.ReactNode;
}

export const getTaskAccessPermissionsWidgets = (taskTemplate: TaskTemplate): TASK_ACCESS_PERMISSION_WIDGETS[] => {
  const taskExecutePermissionId = taskTemplate?.execute_permission;
  const taskAssignPermissionId = taskTemplate?.assign_permission;
  const taskUndoPermissionId = taskTemplate?.undo_permission;
  const taskSupervisePermissionId = taskTemplate?.supervise_permission;

  return [
    {
      title: "Perform",
      description: "Can perform this task",
      permissionId: taskExecutePermissionId,
      icon: <TaskAltIcon color="secondary" />,
    },
    {
      title: "Assign",
      description: "Can assign this task",
      permissionId: taskAssignPermissionId,
      icon: <AssignmentIndIcon color="secondary" />,
    },
    {
      title: "Undo",
      description: "Can undo this task",
      permissionId: taskUndoPermissionId,
      icon: <UndoIcon color="secondary" />,
    },
    {
      title: "Supervise",
      description: "Can supervise this task",
      permissionId: taskSupervisePermissionId,
      icon: <SupervisorAccountIcon color="secondary" />,
    },
  ];
};
